import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Grid
} from "@mui/material";
import { Icon } from "../../../icons";
import { COLORS, TYPOGRAPHY } from "../../../constants/color";
import { useAuth } from "../../../context/authContext";
import axios from "axios";
import { url } from "../../../constants/url";
import ImagePreviewModal from "../../../components/ui/ImagePreviewModal";

// Divider Line Component
const DividerLine = () => (
  <Box sx={{ borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`, my: 2 }} />
);

// Helper function to get full image URL
const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;

  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  if (imagePath.startsWith("data:image")) {
    return imagePath;
  }

  const cleanPath = imagePath.startsWith("/")
    ? imagePath.substring(1)
    : imagePath;
  return `${url}/${cleanPath}`;
};

// Signature Display Component
const SignaturesDisplay = ({ signatures }) => {
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/');
    const timeStr = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} | ${timeStr}`;
  };

  const { siteEngineer, pmcEngineer, pmc } = signatures || {};

  return (
    <Box sx={{ mt: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT, mb: 3 }}>
        Signatures
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          textAlign: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Site Engineer Signature */}
        <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
          {siteEngineer?.signature ? (
            <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="img"
                src={getFullImageUrl(siteEngineer.signature)}
                alt="Site Engineer Signature"
                sx={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          ) : (
            <Box sx={{
              height: 80,
              borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center'
            }} />
          )}
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}>
            Site Engineer
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.PRIMARY, mt: 0.5, fontWeight: 500 }}>
            {siteEngineer?.name || "-"}
          </Typography>
          {siteEngineer?.signedAt && (
            <Typography sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}>
              {formatDateTime(siteEngineer.signedAt)}
            </Typography>
          )}
        </Box>

        {/* PMC Engineer Signature */}
        <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
          {pmcEngineer?.signature ? (
            <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="img"
                src={getFullImageUrl(pmcEngineer.signature)}
                alt="PMC Engineer Signature"
                sx={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          ) : (
            <Box sx={{
              height: 80,
              borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center'
            }} />
          )}
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}>
            PMC Engineer
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.PRIMARY, mt: 0.5, fontWeight: 500 }}>
            {pmcEngineer?.name || "-"}
          </Typography>
          {pmcEngineer?.signedAt && (
            <Typography sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}>
              {formatDateTime(pmcEngineer.signedAt)}
            </Typography>
          )}
        </Box>

        {/* PMC Signature */}
        <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
          {pmc?.signature ? (
            <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="img"
                src={getFullImageUrl(pmc.signature)}
                alt="PMC Signature"
                sx={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          ) : (
            <Box sx={{
              height: 80,
              borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center'
            }} />
          )}
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}>
            PMC
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.PRIMARY, mt: 0.5, fontWeight: 500 }}>
            {pmc?.name || "-"}
          </Typography>
          {pmc?.signedAt && (
            <Typography sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}>
              {formatDateTime(pmc.signedAt)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Skeleton Loader Component
const SkeletonLoader = () => (
  <Box sx={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column" }}>
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <CircularProgress size={60} />
      <Typography sx={{ mt: 2, color: COLORS.SECONDARY }}>
        Loading report data...
      </Typography>
    </Box>
  </Box>
);

// Photo Container Component with Flexbox
const PhotoContainer = ({ photos, entryIndex }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", title: "" });

  if (!photos || photos.length === 0) return null;

  return (
    <Box sx={{ mb: 2, mt: 2 }}>
      <Grid container spacing={2}>
        {photos.map((photo, photoIndex) => {
          if (!photo.photoPath) return null;
          const imageUrl = getFullImageUrl(photo.photoPath);

          return (
            <Grid item xs={12} sm={6} md={4} key={photoIndex}>
              <Box
                onClick={() => {
                  setSelectedImage({ url: imageUrl, title: photo.remarks || "" });
                  setPreviewOpen(true);
                }}
                sx={{
                  position: "relative",
                  border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  borderRadius: 1,
                  overflow: "hidden",
                  height: "180px",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#f5f5f5",
                  cursor: "zoom-in",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.02)",
                    borderColor: COLORS.PRIMARY,
                  }
                }}
              >
                {/* Image with fixed dimensions and object-fit: contain */}
                <Box
                  component="img"
                  src={imageUrl}
                  alt={`Entry ${entryIndex + 1} Photo ${photoIndex + 1}`}
                  sx={{
                    width: "100%",
                    height: photo.remarks ? "130px" : "100%", // Fixed height for image
                    objectFit: "contain", // Show full image without cropping
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Found";
                  }}
                />

                {/* Photo Remarks */}
                {photo.remarks && (
                  <Box sx={{
                    backgroundColor: "#fff",
                    color: COLORS.TEXT,
                    p: 1,
                    borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    height: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}>
                    <Typography sx={{
                      fontSize: "0.75rem",
                      color: COLORS.TEXT,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical"
                    }}>
                      {photo.remarks}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <ImagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageUrl={selectedImage.url}
        title={selectedImage.title}
      />
    </Box>
  );
};

// Solution Renderer Component
const SolutionRenderer = ({ solution }) => {
  if (!solution) return null;

  try {
    if (typeof solution === 'string' && solution.trim().startsWith('{') && solution.trim().endsWith('}')) {
      const structured = JSON.parse(solution);
      if (structured.isStructured) {
        if (structured.type === 'points' && Array.isArray(structured.data)) {
          return (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {structured.data.map((point, i) => (
                <Typography component="li" key={i} sx={{ color: COLORS.SECONDARY, mb: 0.5 }}>
                  {point}
                </Typography>
              ))}
            </Box>
          );
        }
        if (structured.type === 'table' && Array.isArray(structured.data)) {
          return (
            <Box sx={{ mt: 1, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                <tbody>
                  {structured.data.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} style={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, padding: '8px', fontSize: '0.875rem', color: COLORS.SECONDARY }}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          );
        }
      }
    }
  } catch (e) {
    // Fallback to text
  }

  return (
    <Typography sx={{ color: COLORS.SECONDARY, whiteSpace: 'pre-wrap' }}>
      {solution}
    </Typography>
  );
};

// Main Component
const SelfVisitReportViewModal = ({ open, onClose, reportId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const isComponentMounted = useRef(true);
  const statusStyles = {
    SIGNPENDING: { bgcolor: "#A5E3C2", color: "#fff" },
    FINALREPORT: { bgcolor: "#8CD47E", color: "#fff" },
    DRAFT: { bgcolor: "#F8D66D", color: "#fff" },
  };
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!open || !reportId) return;

      try {
        setLoading(true);
        setError(null);
        setReport(null);

        const response = await axios.get(
          `${url}/api/self-visit/fetch/${reportId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (isComponentMounted.current) {
          if (response.data?.data) {
            setReport(response.data.data);
          } else {
            setError("No report data found");
          }
        }
      } catch (err) {
        console.error("Error fetching self-visit report:", err);
        if (isComponentMounted.current) {
          setError(err.response?.data?.message || "Failed to load report");
        }
      } finally {
        if (isComponentMounted.current) {
          setLoading(false);
        }
      }
    };

    if (open) {
      fetchReportData();
    } else {
      setReport(null);
      setError(null);
    }
  }, [open, reportId, token]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/');
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);

      const response = await axios.get(
        `${url}/api/self-visit/download-report/${reportId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const date = new Date(report.dateOfVisit || report.reportDate || new Date());
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const cleanProjectName = (report.projectName || "SiteVisit")
        .replace(/[^a-zA-Z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "")
        .substring(0, 50);

      link.download = `SVR_${cleanProjectName}_${year}_${month}_${day}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {
      console.error("Error downloading report:", err);
      alert(err.response?.data?.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1299,
        }}
      />

      {/* Modal Sidebar - Full height from right side */}
      <Box
        sx={{
          width: { xs: "100%", sm: "70%", md: "60%" },
          height: "100vh",
          position: "fixed",
          top: 0,
          right: open ? 0 : "-100%",
          bgcolor: "#fff",
          zIndex: 1300,
          transition: "right 0.3s ease",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header with WMR-style colors */}
        <Box
          sx={{
            p: 3,
            pb: 2,
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            bgcolor: "#fff",
            flexShrink: 0,
          }}
        >
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
            flexWrap: "wrap",
            gap: 2,
          }}>
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: "1 1 auto",
              minWidth: 200,
              flexWrap: "wrap",
            }}>
              <Typography sx={{
                fontSize: "20px",
                lineHeight: "26px",
                fontWeight: 600,
                color: COLORS.PRIMARY,
              }}>
                Site Visit Report
              </Typography>

              {/* Status Badge - Add this */}
              {report?.status && (
                <Chip
                  label={report.status === "SIGNPENDING" ? "Sign Pending" :
                    report.status === "FINALREPORT" ? "Final Report" : "Draft"}
                  size="small"
                  sx={{
                    ...(statusStyles[report.status] || statusStyles.DRAFT),
                    height: 28,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    '& .MuiChip-label': {
                      padding: '0 12px',
                      letterSpacing: '0.5px'
                    }
                  }}
                />
              )}
            </Box>

            <Box sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexShrink: 0,
            }}>
              {/* Download Button - Only for FINALREPORT status */}
              {report?.status === "FINALREPORT" && (
                <Button
                  variant="contained"
                  onClick={handleDownloadPDF}
                  disabled={downloading || !report}
                  startIcon={<Icon name="download1" size={18} />}
                  sx={{
                    bgcolor: COLORS.SECONDARY,
                    color: "#fff",
                    px: 2,
                    py: 0.8,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    minWidth: 140,
                    "&:hover": { bgcolor: "#5a1c7e" },
                    "&.Mui-disabled": { bgcolor: COLORS.LIGHT_SECONDARY },
                  }}
                >
                  {downloading ? <CircularProgress size={24} color="inherit" /> : "Download PDF"}
                </Button>
              )}

              <IconButton
                onClick={onClose}
                sx={{
                  p: 0.5,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.05)" }
                }}
              >
                <Icon name="close" color={COLORS.ERROR} />
              </IconButton>
            </Box>
          </Box>

          {/* Project Information - WMR style */}
          <Box sx={{ mt: 2 }}>
            {/* First Row: Project Name + Visit Date */}
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              flexWrap: "wrap",
              gap: 1,
            }}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: "1 1 200px",
              }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.TEXT }}>
                  Project Name:
                </Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.SECONDARY }}>
                  {loading ? "Loading..." : (report?.projectName || "N/A")}
                </Typography>
              </Box>

              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: "1 1 200px",
              }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.TEXT }}>
                  Visit Date:
                </Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.SECONDARY }}>
                  {loading ? "Loading..." : (report?.dateOfVisit ? formatDate(report.dateOfVisit) : "N/A")}
                </Typography>
              </Box>
            </Box>

            {/* Second Row: Location + PMC */}
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              flexWrap: "wrap",
              gap: 1,
            }}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: "1 1 200px",
              }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.TEXT }}>
                  Location:
                </Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.SECONDARY }}>
                  {loading ? "Loading..." : (report?.location || "N/A")}
                </Typography>
              </Box>

              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: "1 1 200px",
              }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.TEXT }}>
                  PMC:
                </Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.SECONDARY }}>
                  {loading ? "Loading..." : (report?.userDetails?.name || "N/A")}
                </Typography>
              </Box>
            </Box>

            {/* Third Row: Developer + Report Date */}
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              flexWrap: "wrap",
              gap: 1,
            }}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: "1 1 200px",
              }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.TEXT }}>
                  Developer:
                </Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.SECONDARY }}>
                  {loading ? "Loading..." : (report?.developers || "N/A")}
                </Typography>
              </Box>

              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: "1 1 200px",
              }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.TEXT }}>
                  Report Date:
                </Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.SECONDARY }}>
                  {loading ? "Loading..." : (report?.reportDate ? formatDate(report.reportDate) : "N/A")}
                </Typography>
              </Box>
            </Box>

            {/* Fourth Row: Purpose of Visit */}
            <Box sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.5,
            }}>
              <Typography sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: COLORS.TEXT,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}>
                Purpose of Visit:
              </Typography>
              <Typography sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: COLORS.SECONDARY,
                lineHeight: 1.4,
              }}>
                {loading ? "Loading..." : (report?.purposeOfVisit || "N/A")}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Scrollable Content Area */}
        <Box sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
        }}>
          {loading ? (
            <SkeletonLoader />
          ) : error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : report ? (
            <>
              {/* Report Entries - Matching PDF format */}
              <Box sx={{ mb: 4 }}>
                <Typography sx={{
                  fontSize: TYPOGRAPHY.H3.fontSize,
                  fontWeight: TYPOGRAPHY.H3.fontWeight,
                  color: COLORS.TEXT,
                  mb: 3
                }}>
                  Report Entries
                </Typography>

                {report.categories && report.categories.length > 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {report.categories.map((category, cIdx) => (
                      <Box key={cIdx}>
                        {category.name && category.name.toLowerCase() !== 'uncategorized' && (
                          <Typography sx={{
                            fontSize: TYPOGRAPHY.H3.fontSize,
                            fontWeight: TYPOGRAPHY.H3.fontWeight,
                            color: COLORS.PRIMARY,
                            mt: 2,
                            mb: 2,
                            pb: 1,
                            borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`
                          }}>
                            {category.name}
                          </Typography>
                        )}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {category.entries.map((entry, index) => {
                            const displayTitle = entry.details || entry.title || 'No details provided';

                            return (
                              <Box key={index}>
                                {/* Entry Title - No underline, details as title */}
                                <Typography sx={{
                                  fontSize: TYPOGRAPHY.H4.fontSize,
                                  fontWeight: TYPOGRAPHY.H4.fontWeight,
                                  color: COLORS.PRIMARY,
                                  mb: 1,
                                }}>
                                  <span style={{ color: COLORS.PRIMARY, fontWeight: 700 }}>{index + 1}.</span> {displayTitle}
                                </Typography>

                                {/* Entry Remarks/Solution */}
                                {entry.remarks && (
                                  <Box sx={{ mb: 2, pl: 2 }}>
                                    <SolutionRenderer solution={entry.remarks} />
                                  </Box>
                                )}

                                {/* Photos - Using Flexbox for exactly 2 images per row */}
                                <PhotoContainer photos={entry.photos} entryIndex={index} />

                                {/* Divider between entries (except last) */}
                                {index < category.entries.length - 1 && (
                                  <DividerLine />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                        {/* Divider between categories (except last) */}
                        {cIdx < report.categories.length - 1 && (
                          <Box sx={{ borderTop: `2px dashed ${COLORS.LIGHT_SECONDARY}`, my: 4 }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : report.entries && report.entries.length > 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {report.entries.map((entry, index) => {
                      const displayTitle = entry.details || entry.title || 'No details provided';

                      return (
                        <Box key={index}>
                          {/* Entry Title - No underline, details as title */}
                          <Typography sx={{
                            fontSize: TYPOGRAPHY.H4.fontSize,
                            fontWeight: TYPOGRAPHY.H4.fontWeight,
                            color: COLORS.PRIMARY,
                            mb: 1,
                          }}>
                            <span style={{ color: COLORS.PRIMARY, fontWeight: 700 }}>{index + 1}.</span> {displayTitle}
                          </Typography>

                          {/* Entry Remarks/Solution */}
                          {entry.remarks && (
                            <Box sx={{ mb: 2, pl: 2 }}>
                              <SolutionRenderer solution={entry.remarks} />
                            </Box>
                          )}

                          {/* Photos - Using Flexbox for exactly 2 images per row */}
                          <PhotoContainer photos={entry.photos} entryIndex={index} />

                          {/* Divider between entries (except last) */}
                          {index < report.entries.length - 1 && (
                            <DividerLine />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography sx={{
                    color: COLORS.MUTED_TEXT,
                    textAlign: "center",
                    py: 3,
                    fontStyle: "italic",
                  }}>
                    No entries found in this report
                  </Typography>
                )}
              </Box>

              {/* Admin Remarks - if exists */}
              {report.adminRemarks && (
                <Box sx={{ mb: 4 }}>
                  <Typography sx={{
                    fontSize: TYPOGRAPHY.H4.fontSize,
                    fontWeight: TYPOGRAPHY.H4.fontWeight,
                    color: COLORS.PRIMARY,
                    mb: 1
                  }}>
                    Admin Remarks
                  </Typography>
                  <Box sx={{
                    p: 2,
                    bgcolor: `${COLORS.LIGHT_SECONDARY}15`,
                    borderRadius: 1,
                    borderLeft: `3px solid ${COLORS.SECONDARY}`
                  }}>
                    <Typography sx={{
                      fontSize: TYPOGRAPHY.BODY_STANDARD.fontSize,
                      color: COLORS.SECONDARY
                    }}>
                      {report.adminRemarks}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Signatures Section */}
              <SignaturesDisplay signatures={report.signatures} />
            </>
          ) : null}
        </Box>
      </Box>
    </>
  );
};

// Export the component
export default SelfVisitReportViewModal;